/**
 * Regroupement des lignes d'exécution par poste voté, pour l'écran « Exercice ».
 *
 * Le noyau livre une ligne par sous-poste (compte Zoho ou générique) avec son
 * `posteParent` ; l'écran les lit par poste, avec un sous-total, les comptes
 * étant dépliés en dessous. Fonction pure : le budget voté reste calculé au
 * poste par le noyau.
 */

import { A_CLASSER, A_CLASSER_RECETTES, sommeExacte, type ExecutionPoste } from '@scc/core';
import { libellePosteOuHorsBudget, rangPoste } from '../postes.js';

export interface GroupePoste {
  /** Poste de la nomenclature, `hors_budget` ou `a_classer`. */
  readonly poste: string;
  readonly libelle: string;
  /** Lignes non nulles du groupe, générique en tête puis comptes. */
  readonly lignes: readonly ExecutionPoste[];
  /** Somme exacte de toutes les lignes du groupe, nulles comprises. */
  readonly sousTotal: number;
}

/** Les sorties non classées forment une ligne seule, sans en-tête de poste. */
export const estALClasser = (poste: string): boolean => poste === A_CLASSER || poste === A_CLASSER_RECETTES;

/**
 * Regroupe des lignes d'exécution par `posteParent`. Les lignes à zéro sont
 * masquées ; un groupe n'est gardé que si son sous-total n'est pas nul ou si
 * son poste figure dans `budgetes` (un poste voté reste visible à zéro).
 */
export function grouperLignesParPoste(lignes: readonly ExecutionPoste[], budgetes: ReadonlySet<string>): GroupePoste[] {
  const parPoste = new Map<string, ExecutionPoste[]>();
  for (const l of lignes) {
    const poste = l.posteParent ?? l.poste;
    parPoste.set(poste, [...(parPoste.get(poste) ?? []), l]);
  }
  return [...parPoste.entries()]
    .map(([poste, membres]) => ({
      poste,
      libelle: libellePosteOuHorsBudget(poste),
      lignes: membres.filter((l) => l.realise !== 0),
      sousTotal: sommeExacte(membres.map((l) => l.realise)),
    }))
    .filter((g) => g.sousTotal !== 0 || g.lignes.length > 0 || budgetes.has(g.poste))
    .sort((a, b) => rangPoste(a.poste) - rangPoste(b.poste) || a.poste.localeCompare(b.poste));
}

/**
 * Compte à afficher dans l'en-tête même du poste quand il porte seul le
 * sous-total (les autres lignes sont à zéro) : l'en-tête et le compte feraient
 * sinon deux lignes au même montant. `undefined` si le groupe a plusieurs comptes.
 */
export function compteUnique(g: GroupePoste): ExecutionPoste | undefined {
  const seule = g.lignes[0];
  return g.lignes.length === 1 && seule !== undefined && seule.realise === g.sousTotal ? seule : undefined;
}
